// <Avatar member={m} size={40}/>
//
// Renders a member's profile image if they have one, otherwise their
// initials fallback. The GET /members/:id/avatar endpoint is behind
// requireAuth, so <img src> alone won't work — the Authorization header
// isn't attached to image requests. This component fetches the image
// via api.getAvatarUrl(), converts the blob to an object URL, and
// renders that. The object URL is revoked on unmount to avoid leaks.
//
// The `avatarUpdatedAt` timestamp doubles as a cache-buster query param
// so a fresh upload immediately invalidates the browser cache.

import React, { useEffect, useState } from "react";
import { api } from "../api";

const cache = new Map();   // key: `${id}:${ts}` → object URL. Cheap in-memory
                           // dedup so the same avatar isn't fetched N times
                           // when it appears in a list.

export default function Avatar({ member, size = 40, className = "", style }) {
  const [url, setUrl] = useState(null);

  const hasAvatar = !!member?.avatarUpdatedAt;
  const cacheKey = hasAvatar ? `${member.id}:${new Date(member.avatarUpdatedAt).getTime()}` : null;

  useEffect(() => {
    if (!hasAvatar) { setUrl(null); return; }
    if (cache.has(cacheKey)) {
      setUrl(cache.get(cacheKey));
      return;
    }
    let alive = true;
    let objectUrl = null;
    (async () => {
      try {
        const blob = await api.fetchAvatarBlob(member.id);
        if (!alive) return;
        objectUrl = URL.createObjectURL(blob);
        cache.set(cacheKey, objectUrl);
        setUrl(objectUrl);
      } catch {
        // Silent — falls back to initials.
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  const initials = member?.avatar || "?";
  const baseStyle = {
    width: size, height: size,
    borderRadius: "50%",
    display: "inline-grid",
    placeItems: "center",
    background: "var(--panel-2)",
    color: "var(--text)",
    fontSize: Math.round(size * 0.42),
    fontWeight: 800,
    letterSpacing: "0.02em",
    overflow: "hidden",
    flexShrink: 0,
    ...style
  };

  if (url) {
    return (
      <img
        src={url}
        alt={member?.name || ""}
        className={className}
        style={{ ...baseStyle, objectFit: "cover", background: "var(--panel-2)" }}
      />
    );
  }
  return (
    <span className={className} style={baseStyle}>{initials}</span>
  );
}
